// ─── Fermées avec une réaction (agent v2 `reactions`, 2026-10-30) ───────────
//
// Owner spec (docs/agent-v2/SPEC.md « REACT INSTEAD OF REPLYING »): when a
// client closes (« merci », « ok ghda nsift lflous »), the agent answers with
// ONE WhatsApp reaction and no text. « LOG + DASHBOARD: count "closed with
// reaction" per day. Audit: list of the last 20 react-only decisions. »
//
// Read-only. The reaction itself also shows as a chip under her bubble in the
// chat view, and the draft line reads « Fermée avec une réaction 👍 ».
// The follow-up text is NOT written here — it is the client-promise card's
// (worker composePromiseFollowUp), the same function the sweep sends with.
import { useQuery } from '@tanstack/react-query';
import { whatsappApi, type ReactionDecisionItem } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { SettingGroup } from './_ui';
import { firesLabel } from './ClientPromisesCard';

const MODE_LABEL: Record<string, string> = {
  off: 'Coupé — l’assistant répond par un message, comme avant',
  test: 'Test — seulement sur les numéros de test',
  on: 'Actif pour tous les clients',
};

const ABOUT_LABEL: Record<string, string> = { payment: 'l’avance', photos: 'les photos', other: 'la suite' };

/** « Relance … » in words — never colour alone. */
export function followUpLine(it: Pick<ReactionDecisionItem, 'followup_at' | 'followup_state' | 'promise_about'>): string | null {
  if (!it.followup_at) return null;
  const about = ABOUT_LABEL[it.promise_about ?? 'other'] ?? 'la suite';
  const when = firesLabel(it.followup_at);
  switch (it.followup_state) {
    case 'pending': return `Relance (${about}) prévue ${when} — annulée si elle écrit ou paie avant.`;
    case 'done': return `Relance (${about}) faite, ou annulée car elle a écrit / payé.`;
    case 'replaced': return `Relance remplacée par une promesse plus récente.`;
    default: return `Relance (${about}) prévue ${when}.`;
  }
}

/** « jeu. 29 oct. » for a YYYY-MM-DD day. */
function dayLabel(day: string): string {
  const d = new Date(`${day}T12:00:00Z`);
  return d.toLocaleDateString('fr-FR', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });
}

export function ReactionsCard() {
  const { t } = useT();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['agent-v2-reactions'],
    queryFn: () => whatsappApi.agentReactions(),
    refetchInterval: 120_000,
  });
  const items = data?.items ?? [];
  const perDay = data?.per_day ?? [];
  const today = perDay[0]?.count ?? 0;
  const week = perDay.slice(0, 7).reduce((n, d) => n + d.count, 0);
  const mode = data?.mode ?? 'off';

  return (
    <SettingGroup
      title={t('Fermées avec une réaction')}
      summary={isLoading ? t('Chargement…') : `${today} ${t('aujourd’hui')} · ${week} ${t('sur 7 jours')} · ${t(MODE_LABEL[mode] ?? MODE_LABEL.off)}`}
      tone="default"
      defaultOpen={false}
      loading={isLoading}
    >
      <p className="text-[11.5px] text-gray-500 mt-3 leading-snug max-w-2xl">
        {t(
          'Quand une cliente clôt la conversation (merci, ok, « ghda nsift lflous »), l’assistant réagit à son message '
          + '(❤️ 👍 🙏 😍) sans écrire. Jamais sur une question, une demande, une plainte, un reçu, des photos ou un premier message. '
          + 'Si elle a donné un moment, une seule relance douce part ce jour-là (heures de jour).',
        )}
      </p>

      {data?.table_missing && (
        <p className="text-[11.5px] text-amber-700 mt-2">{t('Journal pas encore créé (migration 275) — les réactions ne sont pas enregistrées ici.')}</p>
      )}
      {isError && (
        <p className="text-[11.5px] text-red-600 mt-3" role="alert">{t('Impossible de charger les réactions.')}</p>
      )}

      {perDay.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" aria-label={t('Fermées avec une réaction, par jour')}>
          {perDay.slice(0, 7).map((d) => (
            <span key={d.day} className="text-[11px] tabular-nums rounded-md border border-gray-100 bg-gray-50 px-2 py-1 text-gray-600">
              {dayLabel(d.day)} · <strong className="text-gray-800">{d.count}</strong>
            </span>
          ))}
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <p className="text-[12px] text-gray-400 mt-3">{t('Aucune conversation fermée par une réaction pour le moment.')}</p>
      )}

      <div className="mt-3 space-y-2">
        {items.map((it) => {
          const follow = followUpLine(it);
          return (
            <div key={it.id} className="rounded-lg border border-gray-100 bg-white px-3.5 py-2.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[13px] font-semibold text-gray-800 truncate">
                  <span aria-hidden className="mr-1.5">{it.emoji}</span>
                  {it.customer_name || it.chat_id.replace(/@.*$/, '')}
                </span>
                <span className="text-[11px] text-gray-400 tabular-nums shrink-0">{firesLabel(it.created_at)}</span>
              </div>
              {it.client_text && <p className="text-[11.5px] text-gray-600 mt-1 leading-snug">« {it.client_text} »</p>}
              <p className="text-[11px] text-gray-500 mt-1">{t('Pourquoi')} : {it.reason}</p>
              {follow && <p className="text-[11.5px] text-gray-700 mt-1 leading-snug">{t(follow)}</p>}
            </div>
          );
        })}
      </div>
    </SettingGroup>
  );
}
